/**
 * Typed client for the BEAM reporting / forecasting surface.
 *
 * Routes (beam-backend reportsRouter — owner/office gated):
 *   GET /api/reports/pnl/:year            → PnlRow (income / expenses-by-category / net)
 *   GET /api/reports/pnl-detailed/:year   → PnlDetailed (adds labor, gross margin, per-job rollup)
 *   GET /api/reports/1099/:year           → Worker1099Total[] (contractor totals + W-9 risk)
 *   GET /api/reports/forecast/materials   → MaterialForecast (buckets of material lines)
 *
 * These endpoints return computed JSON (numbers, not Prisma rows). The shapes mirror
 * beam-backend/src/domain/reporting.ts + forecasting.ts and are typed locally here rather
 * than via a shared DTO. Bearer token via src/api/client. NEVER imports @smartbox/*.
 *
 * CSV exports (year-end / cpa-year-end / gusto) are web-only and intentionally omitted.
 */

import { get } from './client';

// ---------------------------------------------------------------------------
// P&L
// ---------------------------------------------------------------------------

/** GET /api/reports/pnl/:year — basic profit & loss for a calendar year. */
export interface PnlRow {
  income: number;
  expenseTotal: number;
  net: number;
  expensesByCategory: Record<string, number>;
}

/** One per-job profitability row in the detailed P&L. */
export interface JobProfitRow {
  job_id: string;
  job_name: string | null;
  contracted: number;
  cost: number;
  margin: number;
  marginPct: number | null;
}

/** GET /api/reports/pnl-detailed/:year — adds labor + gross margin + per-job rollup. */
export interface PnlDetailed extends PnlRow {
  laborCost: number;
  costOfWork: number;
  grossMargin: number;
  grossMarginPct: number | null;
  jobs: JobProfitRow[];
}

// ---------------------------------------------------------------------------
// 1099 contractor totals
// ---------------------------------------------------------------------------

/** GET /api/reports/1099/:year — one row per 1099 contractor for the year. */
export interface Worker1099Total {
  worker_id: string;
  full_name: string;
  classification: string;
  w9_on_file: boolean;
  total_paid: number;
  /** 1099 worker at/over $600 with no W-9 on file is a filing risk. */
  needs_w9: boolean;
}

// ---------------------------------------------------------------------------
// Materials forecast
// ---------------------------------------------------------------------------

export interface ForecastMaterialLine {
  material_id: string;
  material_name: string;
  uom_code: string | null;
  planned_quantity: number;
  unit_cost: number | null;
  estimated_cost: number | null;
}

export interface ForecastBucket {
  /** UTC calendar day `YYYY-MM-DD`, or `"unscheduled"`. */
  bucket: string;
  materials: ForecastMaterialLine[];
  estimated_cost: number;
}

/** GET /api/reports/forecast/materials — open-work material requirements, bucketed by day. */
export interface MaterialForecast {
  buckets: ForecastBucket[];
  estimated_cost_total: number;
}

// ---------------------------------------------------------------------------
// API methods
// ---------------------------------------------------------------------------

/** Detailed P&L for a year (labor, gross margin, per-job rollup). */
export async function getPnlDetailed(year: number): Promise<PnlDetailed> {
  return get<PnlDetailed>(`/api/reports/pnl-detailed/${year}`);
}

/** 1099 contractor totals for a year. */
export async function get1099(year: number): Promise<Worker1099Total[]> {
  return get<Worker1099Total[]>(`/api/reports/1099/${year}`);
}

/** Materials forecast across open estimates/jobs. */
export async function getForecast(): Promise<MaterialForecast> {
  return get<MaterialForecast>('/api/reports/forecast/materials');
}
